import { z } from 'zod'

import { MetricPeriod, MetricType } from '../constants/metric'
import { VenueChurnRiskSignal } from '../constants/venueChurnRisk'
import {
  admissionGroupMeasuresSchema,
  aggregateByAttributionItemSchema,
  aggregateByInventoryItemSchema,
  metricSchema,
} from './metric'
import { listQueryZodFields } from './system'

export const adminMetricSchema = metricSchema.omit({
  organization: true,
})

const queryBoolean = z.preprocess((value) => {
  if (typeof value === 'string') {
    const v = value.toLowerCase()
    if (v === 'true' || v === '1') return true
    if (v === 'false' || v === '0') return false
  }
  return value
}, z.boolean())

//input schemas
export const adminMetricsListQuerySchema = z.object({
  ...listQueryZodFields,
  name: z.string().optional(),
  search: z.string().optional(),
  type: z.array(z.nativeEnum(MetricType)).or(z.nativeEnum(MetricType)),
  period: z.nativeEnum(MetricPeriod),
  dateStart: z.coerce.date(),
  dateEnd: z.coerce.date(),
  venueId: z.string().or(z.array(z.string())).optional(),
  attributionLimit: z.coerce.number().int().min(0).max(1000).optional(),
  inventoryLimit: z.coerce.number().int().min(0).max(1000).optional(),
  keepByAttributionId: queryBoolean.optional(),
})

export const adminMetricsListAggregateSchema = z.object({
  metrics: z.array(adminMetricSchema),
  aggregate: admissionGroupMeasuresSchema.partial().optional(),
  aggregateByAttribution: z.array(aggregateByAttributionItemSchema).optional(),
  aggregateByAttributionTotalCount: z.number().int().nonnegative().optional(),
  aggregateByInventory: z.array(aggregateByInventoryItemSchema).optional(),
  aggregateByInventoryTotalCount: z.number().int().nonnegative().optional(),
})

export const adminMetricsListResponseSchema = z.object({
  data: adminMetricsListAggregateSchema,
})

const queryBooleanOptional = z.preprocess((value) => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const lowered = value.toLowerCase()
    if (lowered === 'true') return true
    if (lowered === 'false') return false
  }
  return value
}, z.boolean())

export const venueRevenueRankingsQuerySchema = z.object({
  dateStart: z.coerce.date(),
  dateEnd: z.coerce.date(),
  period: z.nativeEnum(MetricPeriod).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
  skip: z.coerce.number().int().min(0).optional(),
  subscriptionActive: queryBooleanOptional.optional(),
})

export const venueRevenueRankingRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  organization: z.string(),
  organizationName: z.string().nullable(),
  totalSpent: z.number(),
  reservationsSpent: z.number(),
  admissionsSpent: z.number(),
  healthScore: z.number().nullable(),
  subscriptionActive: z.boolean().nullable(),
})

export const venueRevenueRankingsSchema = z.object({
  filters: z.object({
    dateStart: z.coerce.date(),
    dateEnd: z.coerce.date(),
    period: z.nativeEnum(MetricPeriod),
    limit: z.number().int(),
    skip: z.number().int(),
    subscriptionActive: z.boolean().optional(),
  }),
  totalCount: z.number().int().nonnegative(),
  venues: z.array(venueRevenueRankingRowSchema),
})

export const venueRevenueRankingsResponseSchema = z.object({
  data: venueRevenueRankingsSchema,
})

export const venueGrowthQuerySchema = z.object({
  dateStart: z.coerce.date(),
  dateEnd: z.coerce.date(),
})

export const venueGrowthDailyItemSchema = z.object({
  date: z.string(),
  count: z.number().int().nonnegative(),
})

export const venueGrowthVenueRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  organization: z.string(),
  organizationName: z.string().nullable(),
  created: z.coerce.date(),
  subscriptionActive: z.boolean().nullable(),
  isPayingNonSubscribed: z.boolean().nullable(),
})

export const venueGrowthSchema = z.object({
  filters: z.object({
    dateStart: z.coerce.date(),
    dateEnd: z.coerce.date(),
  }),
  count: z.number().int().nonnegative(),
  subscriptionActiveCount: z.number().int().nonnegative(),
  payingNonSubscribedCount: z.number().int().nonnegative(),
  effectivePayingCount: z.number().int().nonnegative(),
  daily: z.array(venueGrowthDailyItemSchema),
  venues: z.array(venueGrowthVenueRowSchema),
})

export const venueGrowthResponseSchema = z.object({
  data: venueGrowthSchema,
})

export const venueChurnRiskSignalSchema = z.enum([
  VenueChurnRiskSignal.REVENUE_DROP,
  VenueChurnRiskSignal.NO_EVENTS_PAST_30D,
  VenueChurnRiskSignal.NO_EVENTS_NEXT_30D,
])

export const venueChurnRiskQuerySchema = z.object({
  dateStart: z.coerce.date(),
  dateEnd: z.coerce.date(),
  period: z.nativeEnum(MetricPeriod).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
  skip: z.coerce.number().int().min(0).optional(),
})

export const venueChurnRiskRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  organization: z.string(),
  organizationName: z.string().nullable(),
  totalSpent: z.number(),
  priorTotalSpent: z.number(),
  revenueChangePct: z.number().nullable(),
  pastEventCount: z.number().int().nonnegative(),
  upcomingEventCount: z.number().int().nonnegative(),
  signals: z.array(venueChurnRiskSignalSchema),
})

export const venueChurnRiskSchema = z.object({
  filters: z.object({
    dateStart: z.coerce.date(),
    dateEnd: z.coerce.date(),
    priorDateStart: z.coerce.date(),
    priorDateEnd: z.coerce.date(),
    period: z.nativeEnum(MetricPeriod),
    limit: z.number().int(),
    skip: z.number().int(),
  }),
  totalCount: z.number().int().nonnegative(),
  venues: z.array(venueChurnRiskRowSchema),
})

export const venueChurnRiskResponseSchema = z.object({
  data: venueChurnRiskSchema,
})
